<template>
    <Search 
        ref="search"  
        @ready="readyLines"               
        @show-all="showAllItems"
        @list-item-selected="itemsSelected"
    />
</template>

<script lang="ts" setup>
    import { onMounted, nextTick, onBeforeUnmount, type Ref, ref, computed, type ComputedRef, watch } from "vue"
    import Search from "@/components/widgets/Search/Search.vue";
    import type { SearchRef } from "@/components/widgets/Search/types";
    import { useFocus } from "@/composables/useFocus";
    import { useApplicationStore } from "@/stores/Application";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { storeToRefs } from 'pinia';
    import { usePlayerStore } from "@/stores/Player";
    import  { 
        type NavigationGuardNext, 
        type RouteLocationNormalizedGeneric, 
        type RouteLocationNormalizedLoadedGeneric, 
        useRouter,
        onBeforeRouteUpdate
    } from "vue-router";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import type {IEventSelected } from "@/components/RowView/types";
    import type { ChannelLiveCardData, ProgramCardData, OnDemandCardData, CreditCardData } from "@/components/Cards/types";
    import { useParentControlStore } from "@/stores/ParentControl";
    import { AppConstants } from "@/common";
    import { useSearchPageStore } from "@/stores/SearchPage";
    import { Strings } from '@/locales/types/enums';
    import { ScreenName } from "@/common/types/screen-names";
    import { useCastPageStore } from "@/stores/CastPage";
   
    const {
        setViewedCastDetailItems
    } = useCastPageStore();

    definePageMeta({
        middleware: ['set-page-indicator', 'fetch-search-page'],
    });

    const { setIsHaveFocusedElement, focusElementLine, focusToHeader } = useFocus();

    const SearchPageStore = useSearchPageStore();
    const {
        getQuery :query
    } = storeToRefs(SearchPageStore);

    const ParentControlStoreModule = useParentControlStore();
    const {
        getIsModalParentVisible: isTypeModeEnabledPin,
    } = storeToRefs(ParentControlStoreModule);

    const PlayerStoreModule = usePlayerStore();
    const {
        startPlayingChannel
    } = PlayerStoreModule;

    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);

    const {
        setPlayBtnUnavailable,
        setViewedDetailItems
    } = useDetailPageStore();

    const ApplicationStore = useApplicationStore();
    const {
        getIsHaveFocusedElement: isHavSelectedIdProgram,
    } = storeToRefs(ApplicationStore);
    const {
        showPreviewSignInModal,
        setIsLoadingPageIndicator,
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const { t } = useI18n();
    const router = useRouter();
    const { $eventBus, $analytics } = useNuxtApp();
    const search: Ref<SearchRef | null> = ref(null);
    const isReadyLines: Ref<boolean> = ref(false);
    const isLoadedComponents = computed((): boolean => search.value !== null);
    const isReadyAll = computed((): boolean => isReadyLines.value && isLoadedComponents.value);
    const { shelfKeys }: typeof AppConstants = AppConstants;

    const readyLines = (): void => {
        isReadyLines.value = true;
    };

    const displaySearchResult: ComputedRef<boolean> = computed(() => query.value.length > 0);

    const focusSearchInput = () => {
        if (search.value) {
            search.value.focusSearchInput();
        }
    };

    const showAllItems = (val: IEventSelected): void => {
        let _path: string;
        switch (val.lineId) {
            case shelfKeys.searchResultChannelsCards:
                _path = linkItems.searchChannels.routerPath;
                break;
            case shelfKeys.searchResultLiveCards:
                _path = linkItems.searchLiveTV.routerPath;
                break;
            case shelfKeys.searchResultMoviesCards:
                _path = linkItems.searchMovies.routerPath;
                break;
            case shelfKeys.searchResultShowsCards:
                _path = linkItems.searchShows.routerPath;
                break;
            case shelfKeys.searchResultCreditsCards:
                _path = linkItems.searchCredits.routerPath;
                break;
            default:
                throw new Error(`Unknown lineId: ${val.lineId}`);
        }

        router.push(_path).catch((error) => {
            Logger.captureError(error as Error);
        });
    };
    const itemsSelected = (val: IEventSelected): void => {
        switch (val.lineId) {
            case shelfKeys.searchResultChannelsCards:
                onPlayChannel(val.item as ChannelLiveCardData);
                break;
        
            case shelfKeys.searchResultLiveCards:
                onLiveSelectedSearch(val.item as ProgramCardData);
                break;

            case shelfKeys.searchResultMoviesCards:
                onMoviesSelectedSearch(val.item as OnDemandCardData);
                break;

            case shelfKeys.searchResultShowsCards:
                onShowsSelectedSearch(val.item as OnDemandCardData);
                break;
            case shelfKeys.searchResultCreditsCards:
                onCastSelectedSearchPage(val.item as CreditCardData);
                break; 
        }
    };
    const onCastSelectedSearchPage = (credit: CreditCardData) => {
        try {
            const creditItem = credit.getCreditItem();
            if (!creditItem) {
                throw new Error("СreditItem is null:" + JSON.stringify(credit));
            }

            const personId = credit.getPersonId();
            if (!personId) {
                throw new Error("The person ID is null:" + JSON.stringify(credit));
            }

            setViewedCastDetailItems(creditItem);

            void router.push(`${linkItems.detailCast.routerPath}/${personId}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const onPlayChannel = (item: ChannelLiveCardData) =>{
        if (isGuest.value) {
            setTimeout(() => {
                showPreviewSignInModal(true);
            }, 150);
            return;
        }
        void startPlayingChannel(item);
    };

    const onShowsSelectedSearch = (item: OnDemandCardData): void => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);
            void router.push(`${linkItems.detailsVodSeries.routerPath}/${item.getId()}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const onMoviesSelectedSearch = (item: OnDemandCardData): void => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);
            void router.push(`${linkItems.detailsVodMovies.routerPath}/${item.getId()}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const onLiveSelectedSearch = (item: ProgramCardData) => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);

            // Disable play btn
            setPlayBtnUnavailable(true);
            void router.push(`${linkItems.detailsPrograms.routerPath}/${playItem?.id ? playItem?.id : ''}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };

    const focusElement = () => {
        if (isHavSelectedIdProgram.value) {
            focusElementLine();
        } else {
            focusSearchInput();
        }
    };

    const handlerReady = (): void => {
        void nextTick(()=>{
            setIsHaveFocusedElement();
            if (!isTypeModeEnabledPin.value) {
                focusElement();
            }
        });
    };

    onMounted(() => {
        $eventBus.on("leave-header", focusSearchInput);
        if (!displaySearchResult.value) {
            focusToHeader();
        }

        void nextTick(() => {
            $analytics.createScreenEvent(ScreenName.SEARCH_PAGE, ScreenName.SEARCH_PAGE);
        })
    });

    onBeforeUnmount(() => {
        $eventBus.off("leave-header", focusSearchInput);
    });

    onBeforeRouteUpdate((_to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext) => {
        next();
        setIsLoadingPageIndicator(false);
    });

    watch(
        isReadyAll, 
        (value: boolean) => {
            if (value) {
                handlerReady();
            }
        }
    );
</script>
